import './PaidMediaImpact.scss';
import { ReactElement, useCallback, useState } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { Bar, BarChart, Legend, ResponsiveContainer } from 'recharts';
import { ChartXAxis } from '@Components/Charts/chart-presets';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { numberFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart/AreaChart';
import { Design, Placement, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { SegmentedControlButton } from '@Components/SegmentedControlButton/SegmentedControlButton/SegmentedControlButton';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { Brand100, Neutral200, Brand50, Neutral300 } from '@src/utils/colors';
import { dateToMddyy } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import {
  DATE_GROUPING_OPTIONS_BRAND_EQUITY,
  formatModelOverviewOutcomeVariable
} from '../BrandEquityUtils';
import { useGroupedByDate } from '../useGroupedByDate';
import { currFormat, standardizeMetricName } from './BrandImpactUtils';

interface PaidMediaImpactProps {
  groupByMetric: string;
  paidMediaImpactAreaChart: PaidMediaAreaChart[];
  paidMediaImpactBarChart: Record<string, PaidMediaBarChart>;
  implicationsData: Record<string, ImplicationData>;
  isQv: boolean;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
}

interface PaidMediaAreaChart {
  date: string;
  PaidMedia: number;
  OtherFactors: number;
}

interface PaidMediaBarChart {
  key: string;
  PaidMedia: number;
  OtherFactors: number;
}

interface ImplicationData {
  spendToIncreaseOnePercent: number;
}

type MetricOptionToolTipMap = Record<string, string>;

const GROUP_BY_METRIC_MAP: MetricOptionToolTipMap = {
  Awareness:
    'Respondents are asked to select from a list of brands which they have seen or heard advertising for recently. If they select your brand, that counts as a positive response.',
  Buzz: 'Respondents are asked to select from a list of brands which they have heard positive references to recently. If they select your brand, that counts as a positive response.',
  Consideration:
    'Respondents are asked to select from a list of brands which they would consider purchasing whenever they’re next in the market. If they select your brand, that counts as a positive response.',
  Intent:
    'Respondents are asked to select from a list of brands which they have ever heard of. If they select your brand, that counts as a positive response.'
};

const capitalizeWords = (s: string): string =>
  s
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b./g, a => a.toUpperCase());

const renderCustomLabel = props => {
  const { x, y, width, value } = props;
  const percentage = `${(value * 100).toFixed(2)}%`;

  return (
    <text x={x + width / 2} y={y} fill='black' textAnchor='middle' dy={-6}>
      {percentage}
    </text>
  );
};

export const PaidMediaImpact = ({
  groupByMetric,
  paidMediaImpactAreaChart,
  paidMediaImpactBarChart,
  implicationsData,
  isQv,
  queryVolumeLongLabel,
  queryVolumeShortLabel
}: PaidMediaImpactProps): ReactElement => {
  const groupByMetricName =
    groupByMetric.toLocaleUpperCase() === 'GQV' ? queryVolumeShortLabel : groupByMetric;
  // A metric with no implication row (all rows dark last quarter, or none returned) leaves an
  // undefined value under an existing key; drop those so the render below can't deref undefined.
  const implicationEntries = Object.entries(implicationsData).filter(([, row]) => row);
  const [selectedToggleOptionWAA, setSelectedToggleOptionWAA] = useState<string>('Over time');
  const [weeklyPaidMediaImpactDateGrouping, setWeeklyPaidMediaImpactDateGrouping] =
    useState<DateGroupingKey>('Week');
  const paidMediaImpactAreaChartFinal = useGroupedByDate(
    paidMediaImpactAreaChart,
    weeklyPaidMediaImpactDateGrouping
  );

  const excelDownloadWeeklyAdAwarenessAreaChart = useCallback(() => {
    exportToExcel(paidMediaImpactAreaChartFinal, `Weekly_${groupByMetric}_Area_Chart`);
  }, [groupByMetric, paidMediaImpactAreaChartFinal]);

  const pngDownloadWeeklyAdAwarenessAreaGraph = useCallback(async () => {
    await downloadPNG('.paid-media-impact .left-left', `Weekly_${groupByMetric}_Area_Graph`);
  }, [groupByMetric]);

  const excelDownloadWeeklyAdAwarenessBarChart = useCallback(() => {
    exportToExcel([paidMediaImpactBarChart], `Weekly_${groupByMetric}_Bar_Chart`);
  }, [groupByMetric, paidMediaImpactBarChart]);

  const pngDownloadWeeklyAdAwarenessBarChart = useCallback(async () => {
    await downloadPNG('.paid-media-impact .left-left', `Weekly_${groupByMetric}_Bar_Chart`);
  }, [groupByMetric]);

  const pngDownloadImplications = useCallback(async () => {
    await downloadPNG('.paid-media-impact .left-right', `${groupByMetric}__Implications`);
  }, [groupByMetric]);

  return (
    <WidgetContainer
      collapsible
      header={
        <>
          How does our paid media impact{' '}
          <span className='paid-media-header-metric-name'>
            {standardizeMetricName(
              groupByMetric,
              true,
              queryVolumeLongLabel,
              queryVolumeShortLabel
            )}
          </span>
          ?
        </>
      }
      subHeader={
        <>
          {`Attribution of ${standardizeMetricName(
            groupByMetric,
            false,
            queryVolumeLongLabel,
            queryVolumeShortLabel
          )} to media investment based on model parameter estimates.`}
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            {`Calculated for each point in time as (${groupByMetricName} at that point in time) - (level of media investments at that point in time) x (model parameter estimates for those media inputs)`}
          </InfoTooltip>
        </>
      }
    >
      <div className='paid-media-impact'>
        <div className='left-left'>
          {paidMediaImpactAreaChartFinal.length !== 0 &&
            Object.keys(paidMediaImpactBarChart).length !== 0 && (
              <ChartContainer
                rightActions={
                  <div className='paid-media-impact-right-actions'>
                    <SegmentedControlButton
                      size={Size.LG}
                      design={Design.NEUTRAL}
                      options={['Total', 'Over time']}
                      selectedOption={selectedToggleOptionWAA}
                      onChange={setSelectedToggleOptionWAA}
                    />
                    <DownloadDropdown
                      onClickOptions={[
                        selectedToggleOptionWAA === 'Over time'
                          ? excelDownloadWeeklyAdAwarenessAreaChart
                          : excelDownloadWeeklyAdAwarenessBarChart,
                        selectedToggleOptionWAA === 'Over time'
                          ? pngDownloadWeeklyAdAwarenessAreaGraph
                          : pngDownloadWeeklyAdAwarenessBarChart
                      ]}
                    />
                  </div>
                }
                title={
                  <>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyPaidMediaImpactDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyPaidMediaImpactDateGrouping(option)}
                    />
                    <div>{groupByMetricName}</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  paidMediaImpactAreaChartFinal[0].date
                )} – ${dateToMddyy(
                  paidMediaImpactAreaChartFinal[paidMediaImpactAreaChartFinal.length - 1].date
                )}`}
                beforeTooltipText={isQv ? 'Search Intent' : 'Positive Response'}
                tooltipText={
                  isQv
                    ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                    : 'Positive response indicates the percentage of survey respondents expressing favorable perceptions or experiences related to each brand health metric.'
                }
              >
                {selectedToggleOptionWAA === 'Over time' && (
                  <AreaChart
                    data={paidMediaImpactAreaChartFinal}
                    xAxisDataKey='date'
                    xAxisTickFormatter={val => dateToMddyy(val)}
                    dateGrouping={weeklyPaidMediaImpactDateGrouping}
                    yAxisTickFormatter={value => (isQv ? value : `${value}%`)}
                    colorOverrides={{
                      // #8254FF and #E5E9F2 have no design-system palette equivalent
                      [`${groupByMetricName} From Paid Media`]: Brand50,
                      [`${groupByMetricName} From Other Factors`]: Neutral300
                    }}
                    areas={[
                      {
                        name: `${groupByMetricName} From Other Factors`,
                        dataKey: 'OtherFactors',
                        toolTip: GROUP_BY_METRIC_MAP[groupByMetric]
                      },
                      {
                        name: `${groupByMetricName} From Paid Media`,
                        dataKey: 'PaidMedia'
                      }
                    ]}
                    tooltipFormatter={val => {
                      if (!val) {
                        return val;
                      }
                      return numberFormatter.format(val, 2);
                    }}
                    tooltipProps={{
                      reverseItems: true
                    }}
                    reverseLegend
                  />
                )}
                {selectedToggleOptionWAA === 'Total' &&
                  Object.values(paidMediaImpactBarChart).map((item, index) => (
                    <div key={index}>
                      <ResponsiveContainer width='100%' height='100%' minWidth={100}>
                        <BarChart
                          width={500}
                          height={300}
                          maxBarSize={200}
                          data={[item]}
                          margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5
                          }}
                        >
                          <ChartXAxis
                            dataKey='key'
                            orientation='top'
                            axisLine={false}
                            tickFormatter={capitalizeWords}
                            tick={
                              groupByMetric === formatModelOverviewOutcomeVariable(item.key)
                                ? {
                                    dy: -15,
                                    fill: Brand100,
                                    fontWeight: 600
                                  }
                                : {
                                    dy: -15,
                                    fill: Brand100,
                                    fontWeight: 400
                                  }
                            }
                          />
                          <Legend iconSize={0} layout='horizontal' />
                          {/* #946DFF and #A8A8A8 have no design-system palette equivalent */}
                          <Bar
                            dataKey='PaidMedia'
                            fill={
                              groupByMetric === formatModelOverviewOutcomeVariable(item.key)
                                ? '#946DFF'
                                : Neutral200
                            }
                            name='Paid Media'
                            label={renderCustomLabel}
                          />
                          <Bar
                            dataKey='OtherFactors'
                            fill={
                              groupByMetric === formatModelOverviewOutcomeVariable(item.key)
                                ? '#A8A8A8'
                                : Neutral200
                            }
                            name='Other Factors'
                            label={renderCustomLabel}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
              </ChartContainer>
            )}
        </div>
        <div className='left-right'>
          <ChartContainer
            title='Implications'
            rightActions={
              <DownloadDropdown menuOptions={['PNG']} onClickOptions={[pngDownloadImplications]} />
            }
          >
            <div className='brand-equity-implication'>
              <div className='title-row-style'>
                <div className='title-style-left'>Investment Increase</div>
                <div className='title-style-right'>Brand Health Impact</div>
              </div>
              {implicationEntries.map(([key, row]) => (
                <div key={key} className='row-style'>
                  <div
                    className={
                      standardizeMetricName(
                        key,
                        false,
                        queryVolumeLongLabel,
                        queryVolumeShortLabel
                      ) === groupByMetric
                        ? 'spend-text row-style-color-spend-text'
                        : 'spend-text'
                    }
                  >
                    {`+ ${currFormat(row.spendToIncreaseOnePercent, 0)}`}
                    <span className='cadence-text'>{' per week'}</span>
                  </div>
                  <div
                    className={
                      standardizeMetricName(
                        key,
                        false,
                        queryVolumeLongLabel,
                        queryVolumeShortLabel
                      ) === groupByMetric
                        ? 'arrow-icon row-style-color-arrow-icon'
                        : 'arrow-icon'
                    }
                  >
                    <MdArrowForward />
                  </div>
                  <div
                    className={
                      standardizeMetricName(
                        key,
                        false,
                        queryVolumeLongLabel,
                        queryVolumeShortLabel
                      ) === groupByMetric
                        ? 'metric-text row-style-color-metric-text'
                        : 'metric-text'
                    }
                  >
                    {' '}
                    {`+1% ${standardizeMetricName(
                      key,
                      false,
                      queryVolumeLongLabel,
                      queryVolumeShortLabel
                    )}`}
                  </div>
                </div>
              ))}
              <div className='end-text-row'>
                <div className='end-text-row-style'>
                  The required investment to increase a brand health metric by 1% assumes an
                  optimally efficient allocation based on channel median spend and composition from
                  the most recent quarter
                </div>
              </div>
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
